/**
 * Read-path timeout helper.
 *
 * Edge Function calls (internal/edgeFunctions.ts) have carried a bounded
 * AbortController since 0.2.0. Direct PostgREST reads — and the cloud-save
 * upsert, which is a direct write outside the Edge Function layer — never
 * did: a stalled connection hangs the calling getLeaderboard /
 * getAchievements / cloudSave.get / cloudSave.set / init's profile load
 * forever, since neither `fetch` nor the postgrest-js client applies a
 * default timeout.
 *
 * postgrest-js builders accept `.abortSignal(signal)`, but an aborted
 * query does NOT reliably reject the awaited builder the way an aborted
 * `fetch` does: most paths RESOLVE with `{ data: null, error: {
 * message: 'AbortError: ...' } }` (the abort surfaces as a request
 * error, not a thrown exception), while some paths still reject. Both
 * outcomes are handled here so every call site gets one typed timeout
 * error regardless of which shape its query happens to hit.
 *
 * AbortSignal.timeout() would be shorter, but Safari < 15.4 doesn't
 * implement it and the SDK still has to run there — so the controller
 * and timer are wired up by hand, same as edgeFunctions.ts.
 *
 * The timer is cleared in `finally`, not just on the success path:
 * beyond canceling a late abort() after the result already arrived, an
 * uncleared 10s timer keeps a Node consumer's event loop alive up to
 * READ_TIMEOUT_MS after a single fast read — a script doing one query
 * would hang around for the full bound before exiting.
 *
 * NETWORK_ERROR + "<label> timed out after 10s" deliberately mirrors the
 * Edge Function timeout shape from 0.2.0 (see internal/edgeFunctions.ts)
 * — callers that already handle one shape handle both.
 */
import { VibecadeError, VibecadeErrorCode } from '../types';

export const READ_TIMEOUT_MS = 10_000;

/**
 * Run a single query execution under a bound. `run` receives the
 * AbortSignal to attach via the builder's `.abortSignal(signal)`;
 * construct a fresh AbortController per call — never reuse one that may
 * already have fired. A reused, already-fired controller hands the next
 * query a signal that is already in the aborted state, killing it
 * instantly instead of giving it its own fresh 10s window.
 *
 * A result that resolves with data and no error is returned unchanged
 * even when the timer raced and fired first — a genuinely-completed
 * query must never be misreported as a timeout.
 */
export async function withReadTimeout<T>(
  label: string,
  run: (signal: AbortSignal) => PromiseLike<T>,
): Promise<T> {
  const controller = new AbortController();
  // Set synchronously by the timer callback before it calls abort(), so
  // the resolve/reject handling below can tell a timeout apart from
  // every other outcome deterministically — an aborted request's
  // error shape isn't consistent across the reject vs. resolve-with-
  // error paths postgrest-js can take, but this flag is.
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, READ_TIMEOUT_MS);

  const timeoutError = () =>
    new VibecadeError(
      VibecadeErrorCode.NetworkError,
      `${label} timed out after ${READ_TIMEOUT_MS / 1000}s`,
    );

  try {
    let result: T;
    try {
      result = await run(controller.signal);
    } catch (e) {
      // Some paths reject the awaited builder on abort — reclassify
      // only when the timer actually fired; any other rejection is the
      // query's real failure and passes through untouched.
      throw timedOut ? timeoutError() : e;
    }
    // The common postgrest-js case: abort resolves the builder instead
    // of rejecting it, carrying the failure as `{ data: null, error:
    // {...} }`. Only reclassify when the timer fired AND the result
    // reports an error — a result with no error is a genuine
    // completion that happened to race the abort, and must be returned
    // as-is rather than reported as a timeout.
    if (timedOut && (result as { error?: unknown } | null | undefined)?.error) {
      throw timeoutError();
    }
    return result;
  } finally {
    clearTimeout(timer);
  }
}
